/**
 * Maps a flag library form into a FHIR `Flag`, and a stored `Flag` back into the row the library
 * table renders.
 *
 * Library flags are practice-level definitions, not chart findings: the `subject` is always the
 * practice's Flag Library Group, never a Patient, so no PHI is involved. `Flag` has no
 * `description` element, so the flag name rides on the coding and the prose sits on `code.text`.
 *
 * Category and priority are separate codings in `category[]`, each on its own CodeSystem. Readers
 * must match on `coding.system` rather than trusting array order.
 *
 * Only the practice-authored name needs a derived code — `slugify` turns "Fall Risk" into
 * "fall-risk". Category and priority carry their seeded codes already, so they are written straight
 * through. `slugify`'s other callers use it for test ids rather than for persisted values, so the
 * tests pin the slug rules here: a change made for a test id must not silently restate every
 * practice's flag codes.
 */

import type { WithId } from "@medplum/core";
import type { CodeableConcept, Coding, Flag } from "@medplum/fhirtypes";

import { LIBRARY_FLAG_IDENTIFIER_SYSTEM } from "@/fhir/constants/identifier";
import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { getCategory } from "@/utils/medplum";
import { slugify } from "@/utils/strings";

import {
  ACTIVE_FLAG_STATUS,
  FLAG_CATEGORY_CODINGS,
  FLAG_CATEGORY_VALUES,
  FLAG_PRIORITY_CODINGS,
  FLAG_PRIORITY_VALUES,
} from "../constants/flags";
import { PracticeFlag, PracticeFlagEntry } from "../interfaces/flags";
import { PracticeFlagFormType } from "../zod-schemas/flags";

/**
 * Builds the `Flag` payload for a library entry.
 *
 * Takes values that have been through `PracticeFlagFormSchema.parse`, so `name` and `description`
 * arrive trimmed and this does not trim them again.
 *
 * @param values - Parsed form values, not raw form state
 * @param groupReference - `Group/{id}` reference to the practice's Flag Library Group
 */
export function buildPracticeFlagResource(
  values: PracticeFlagFormType,
  groupReference: string,
): Flag {
  const { name, description } = values;

  // One entry per coded field, in the order readers see them. Unset fields drop out.
  const category: CodeableConcept[] = [];

  if (values.category) {
    category.push({ coding: [FLAG_CATEGORY_CODINGS[values.category]] });
  }

  if (values.priority) {
    category.push({ coding: [FLAG_PRIORITY_CODINGS[values.priority]] });
  }

  // The code is mirrored onto `identifier` because R4 gives `Flag` no `code` search param, and the
  // conditional create that keeps a code unique per library needs something searchable to match.
  const code = slugify(name);

  return {
    resourceType: "Flag",
    status: ACTIVE_FLAG_STATUS,
    subject: { reference: groupReference },
    identifier: [{ system: LIBRARY_FLAG_IDENTIFIER_SYSTEM, value: code }],
    code: {
      coding: [
        {
          system: QuiltedHealthCodeSystems.LibraryFlag,
          code,
          display: name,
        },
      ],
      ...(description ? { text: description } : {}),
    },
    ...(category.length ? { category } : {}),
  };
}

/**
 * Seeds the flag form from a library row, for an edit.
 *
 * Takes the row rather than the stored `Flag` so the drawer opens on what the table already has in
 * hand, with no read in between. Row and form hold the same four fields, and `null` means the same
 * thing in both — a select the practice left unset.
 *
 * @param flag - A row as `toPracticeFlag` produced it
 */
export function toPracticeFlagFormValues(flag: PracticeFlag): PracticeFlagFormType {
  return {
    name: flag.name,
    category: flag.category,
    priority: flag.priority,
    description: flag.description,
  };
}

/**
 * Builds the `Flag` payload that replaces a stored library entry with edited values.
 *
 * Built from the values rather than merged over the stored entry, so clearing category or priority
 * drops its coding instead of leaving the old one in place. Three things the form does not own are
 * carried across: the logical id, `meta` — which is what keeps the entry in the practice
 * compartment — and `status`, so an edit never revives a deleted entry or retires a live one.
 *
 * A renamed flag gets a re-derived code, here and on the identifier alike. Whether that code is
 * free is the service's question, not this one's.
 *
 * @param existing - The stored entry, as read from the server
 * @param values - Parsed form values, not raw form state
 * @param groupReference - `Group/{id}` reference to the practice's Flag Library Group
 */
export function buildUpdatedPracticeFlagResource(
  existing: WithId<Flag>,
  values: PracticeFlagFormType,
  groupReference: string,
): WithId<Flag> {
  return {
    ...buildPracticeFlagResource(values, groupReference),
    id: existing.id,
    meta: existing.meta,
    status: existing.status,
  };
}

/**
 * Reads a flag's library coding — the one on the flag-library system specifically, so it never picks
 * up the category or priority codings that share the resource.
 *
 * @param flag - Any Flag; library entries carry the coding, patient flags may not
 */
export function getLibraryFlagCoding(flag: Flag): Coding | undefined {
  return flag.code?.coding?.find(
    (coding) => coding.system === QuiltedHealthCodeSystems.LibraryFlag,
  );
}

/**
 * Reads a flag's library code — the value a uniqueness guard matches on.
 *
 * @param flag - Any Flag; library entries carry the coding, patient flags may not
 */
export function getLibraryFlagCode(flag: Flag): string | undefined {
  return getLibraryFlagCoding(flag)?.code;
}

/**
 * Reads one of the coded fields off `Flag.category[]` and narrows it to the enum the UI works in.
 *
 * A code outside the enum reads as unset rather than being passed through: every consumer indexes
 * `FLAG_CATEGORY_CODINGS` or `FLAG_PRIORITY_CODINGS` by this value, so an unknown code — a concept
 * seeded after this build, or hand-edited data — would otherwise crash the row it renders.
 *
 * @param flag - Any Flag; a library entry saved without the field carries no coding for it
 * @param system - The CodeSystem to match on, category and priority sharing `category[]`
 * @param members - The enum's members, in any order
 */
function readCodedField<T extends string>(
  flag: Flag,
  system: string,
  members: readonly T[],
): T | null {
  const code = getCategory(flag.category, system)?.code;

  return members.includes(code as T) ? (code as T) : null;
}

/**
 * Builds the library row for a stored `Flag` — the reverse of `buildPracticeFlagResource`.
 *
 * The name comes off the library coding's `display`, falling back to its `code`, so a row is never
 * blank in the column it is sorted and searched by. Nothing here can throw: a Flag missing any
 * element reads as an empty or unset field, because one malformed entry must not take out the
 * whole library.
 *
 * The chart count is not part of this. It belongs to the library table's row rather than to the
 * entry itself, and `getPracticeFlags` composes it on — so chart-side callers, which never show a
 * count, have nothing to pass and nothing to ignore.
 *
 * @param flag - A library entry as searched from the server
 */
export function toPracticeFlag(flag: WithId<Flag>): PracticeFlagEntry {
  const libraryCoding = getLibraryFlagCoding(flag);

  return {
    id: flag.id,
    name: libraryCoding?.display ?? libraryCoding?.code ?? "",
    description: flag.code?.text ?? "",
    category: readCodedField(flag, QuiltedHealthCodeSystems.FlagCategory, FLAG_CATEGORY_VALUES),
    priority: readCodedField(flag, QuiltedHealthCodeSystems.FlagPriority, FLAG_PRIORITY_VALUES),
  };
}
